import { useCompanionPanelConversationQuery } from '@/hooks/queries/use-companion-panel-conversation-query';
import { Button, Stack, Text } from '@chakra-ui/react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { COMPANION_PANEL_SCROLL_ATTRIBUTE } from './CompanionPanelShell';
import { CompanionPanelTurn } from './CompanionPanelTurn';

/**
 * The conversation, rendered from the conversation query alone. A streamed
 * answer writes into that cache and a persisted one is read from it, so the
 * thread shows the same thing whether the stream is attached or not.
 */

/** Within this distance of the bottom, the reader is still following the answer. */
const FOLLOW_SLACK_PX = 40;

const isAtBottom = (scroller: HTMLElement): boolean =>
  scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight <= FOLLOW_SLACK_PX;

const scrollerOf = (element: HTMLElement | null): HTMLElement | null =>
  element?.closest<HTMLElement>(`[${COMPANION_PANEL_SCROLL_ATTRIBUTE}]`) ?? null;

interface CompanionPanelThreadProps {
  brandId: string;
  /** Asks the same question again, after an interrupted or failed answer. */
  onRetry: (question: string) => void;
  isAnswering: boolean;
}

export const CompanionPanelThread = ({
  brandId,
  onRetry,
  isAnswering,
}: CompanionPanelThreadProps) => {
  const { t } = useTranslation();
  const { data: conversation, isPending, isError, refetch } =
    useCompanionPanelConversationQuery(brandId);
  const turns = conversation?.turns ?? [];

  // The panel (never the page) follows the answer down while it is written,
  // until the reader scrolls up to read; a new question follows it again.
  const endRef = useRef<HTMLDivElement>(null);
  const followRef = useRef(true);
  // Where the panel last scrolled itself: only a move above it is the reader's.
  const autoTopRef = useRef(0);
  const turnCountRef = useRef(turns.length);
  const lastTurn = turns.at(-1);
  const hasTurns = turns.length > 0;

  useEffect(() => {
    const scroller = scrollerOf(endRef.current);
    if (!scroller) return;
    const onScroll = () => {
      // The panel's own scroll can land after the answer grew again: that is
      // not the reader leaving the bottom, a move up is.
      if (isAtBottom(scroller)) followRef.current = true;
      else if (scroller.scrollTop < autoTopRef.current) followRef.current = false;
    };
    scroller.addEventListener('scroll', onScroll, { passive: true });
    // A shorter window scrolls nothing, so the end of the answer drops under
    // the fold: a reader who was following is put back at the bottom.
    const resize = new ResizeObserver(() => {
      if (!followRef.current) return;
      scroller.scrollTop = scroller.scrollHeight;
      autoTopRef.current = scroller.scrollTop;
    });
    resize.observe(scroller);
    return () => {
      scroller.removeEventListener('scroll', onScroll);
      resize.disconnect();
    };
  }, [hasTurns]);

  useEffect(() => {
    const scroller = scrollerOf(endRef.current);
    if (turns.length !== turnCountRef.current) {
      turnCountRef.current = turns.length;
      followRef.current = true;
    } else if (scroller && scroller.scrollTop < autoTopRef.current - 1 && !isAtBottom(scroller)) {
      // The reader moved up and their scroll event has not landed yet.
      followRef.current = false;
    }
    if (!scroller || !followRef.current) return;
    scroller.scrollTop = scroller.scrollHeight;
    autoTopRef.current = scroller.scrollTop;
  }, [turns.length, lastTurn?.answerText.length, lastTurn?.steps.length, lastTurn?.status]);

  if (isPending) {
    return (
      <Text textStyle="agent.meta" color="agent.fgMuted" role="status">
        {t('companion.conversation.loading')}
      </Text>
    );
  }

  if (isError) {
    return (
      <Stack gap="2" align="flex-start">
        <Text textStyle="agent.text" color="agent.fgBody">
          {t('companion.conversation.loadError')}
        </Text>
        <Button variant="outline" size="xs" colorPalette="agent" onClick={() => void refetch()}>
          {t('companion.conversation.reload')}
        </Button>
      </Stack>
    );
  }

  if (turns.length === 0) {
    return (
      <Text textStyle="agent.text" color="agent.fgMuted">
        {t('companion.conversation.empty')}
      </Text>
    );
  }

  return (
    <Stack gap="6">
      {turns.map((turn) => (
        <CompanionPanelTurn
          key={turn.id}
          turn={turn}
          onRetry={onRetry}
          isAnswering={isAnswering}
        />
      ))}
      <div ref={endRef} />
    </Stack>
  );
};
